// components/Records/RecordDisplay/EditableField.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  TextField,
  Typography,
  Switch,
  Box,
  IconButton,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

export type EditableFieldType =
  | "text"
  | "multiline"
  | "number"
  | "integer"
  | "boolean"
  | "date"
  | "datetime"
  | "select";

export type SelectOption = { label: string; value: string | number | boolean };

export interface EditableFieldProps<T> {
  record: T | null;
  field: keyof T;
  label: string;

  variant?: React.ComponentProps<typeof Typography>["variant"];

  multiline?: boolean;
  editable?: boolean;

  type?: EditableFieldType;
  placeholder?: string;

  min?: number;
  max?: number;
  step?: number;

  // for selector
  options?: Array<SelectOption | string>;

  onChange?: (field: keyof T, value: any) => void;
  onSave?: (field: keyof T, value: any) => Promise<void> | void;
}

function isNil(v: any) {
  return v === null || v === undefined;
}

// For <input type="datetime-local">, convert to ISO for Django DateTimeField friendliness
function datetimeLocalToISO(value: string): string {
  // value looks like "2026-02-03T22:15"
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString();
}

function formatForDisplay(type: EditableFieldType, raw: any): string {
  if (raw === null || raw === undefined || raw === "") return "";

  // DATE / DATETIME
  if ((type === "date" || type === "datetime") && typeof raw === "string") {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      const opts: Intl.DateTimeFormatOptions =
        type === "date"
          ? { year: "numeric", month: "short", day: "2-digit" }
          : {
              year: "numeric",
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              // uncomment if you want it explicitly in UK time:
              // timeZone: "Europe/London",
            };

      return new Intl.DateTimeFormat(undefined, opts).format(d);
    }
  }

  // NUMBERS (optional nicety)
  if ((type === "number" || type === "integer") && raw !== "") {
    const n = Number(raw);
    if (!Number.isNaN(n)) return new Intl.NumberFormat(undefined).format(n);
  }

  return String(raw);
}



// When record contains an ISO string, show it in datetime-local format
function isoToDatetimeLocal(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  const pad = (n: number) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());

  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function normalizeWebsiteUrl(raw: any): any {
  if (raw === null || raw === undefined) return raw;

  const s = String(raw).trim();
  if (!s) return ""; // keep blank as blank

  // If user typed just a domain, add scheme so Django URLField accepts it
  if (!/^https?:\/\//i.test(s)) {
    return `https://${s}`;
  }
  return s;
}


const EditableField = <T,>({
  record,
  field,
  label,
  variant = "body1",
  multiline = false,
  editable = true,
  type,
  placeholder,
  min,
  max,
  step,
  options, // ✅ IMPORTANT: destructure options so it exists in scope
  onChange,
  onSave,
}: EditableFieldProps<T>) => {
  const theme = useTheme();

  const inferredType: EditableFieldType = useMemo(() => {
    if (type) return type;
    if (multiline) return "multiline";
    return "text";
  }, [type, multiline]);

  const recordValue = (record as any)?.[field];

  const initialValue = useMemo(() => {
    if (
      inferredType === "datetime" &&
      typeof recordValue === "string" &&
      recordValue.includes("T")
    ) {
      return isoToDatetimeLocal(recordValue);
    }
    return isNil(recordValue) ? "" : recordValue;
  }, [recordValue, inferredType]);

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<any>(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const commitSave = async (nextValue: any) => {
    if (!onSave) return;

    let payload = nextValue;

    if (inferredType === "integer") {
      payload = nextValue === "" ? null : parseInt(String(nextValue), 10);
      if (Number.isNaN(payload)) payload = null;
    }

    if (inferredType === "number") {
      payload = nextValue === "" ? null : Number(nextValue);
      if (Number.isNaN(payload)) payload = null;
    }

    if (inferredType === "datetime" && typeof nextValue === "string" && nextValue) {
      payload = datetimeLocalToISO(nextValue);
    }

    // ✅ Make Django URLField happy for organisation.website
    if (field === "website") {
      payload = normalizeWebsiteUrl(payload);
    }

    await onSave(field, payload);
  };

  const handleBlur = async () => {
    setEditing(false);
    await commitSave(value);
  };

  if (!record) return null;

  // Not editable -> static
  if (!editable) {
    const displayed =
      isNil(recordValue) || recordValue === ""
        ? (placeholder ?? label)
        : formatForDisplay(inferredType, recordValue);

    return (
      <Typography
        variant={variant}
        sx={{ mb: 1, cursor: "default", userSelect: "none" }}
      >
        {displayed}
      </Typography>
    );
  }


  // BOOLEAN: show a switch, save immediately
  if (inferredType === "boolean") {
    const checked = Boolean(recordValue);

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Switch
          checked={checked}
          onChange={async (e) => {
            const next = e.target.checked;
            onChange?.(field, next);
            await commitSave(next);
          }}
        />
        <Typography variant={variant}>{checked ? "Yes" : "No"}</Typography>
      </Box>
    );
  }

  // INTEGER COUNTER: +/- plus number input; saves on blur/enter
  if (inferredType === "integer") {
    const numeric = value === "" ? 0 : parseInt(String(value), 10) || 0;

    const clamp = (n: number) => {
      let out = n;
      if (typeof min === "number") out = Math.max(min, out);
      if (typeof max === "number") out = Math.min(max, out);
      return out;
    };

    const bump = (delta: number) => {
      const next = clamp(numeric + delta);
      setValue(next);
      onChange?.(field, next);
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <IconButton size="small" onClick={() => bump(-1)}>
          <RemoveIcon fontSize="small" />
        </IconButton>

        <TextField
          variant="standard"
          value={value}
          onChange={(e) => {
            const next = e.target.value;
            setValue(next);
            onChange?.(field, next);
          }}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleBlur();
          }}
          inputProps={{ inputMode: "numeric" }}
          sx={{
            width: 90,
            "& .MuiInputBase-input": {
              textAlign: "center",
              ...(theme.typography as any)[variant] || {},
              padding: 0,
            },
          }}
        />

        <IconButton size="small" onClick={() => bump(1)}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  // SELECT (multiple choice): dropdown; saves on change
  if (inferredType === "select") {
    const normalizedOptions: SelectOption[] = (options ?? []).map((opt) =>
      typeof opt === "string" ? { label: opt, value: opt } : opt
    );

    const current = (record as any)?.[field];
    const selected = value ?? current ?? "";

    const findLabel = (v: any) =>
      normalizedOptions.find((o) => o.value === v)?.label ?? "";

    return editing ? (
      <TextField
        select
        variant="standard"
        fullWidth
        autoFocus
        // MUI select uses string values under the hood, so we stringify for the component
        value={selected === null || selected === undefined ? "" : String(selected)}
        onChange={async (e) => {
          const raw = e.target.value;

          // Recover original typed value (string/number/bool) by matching option.value
          const matched = normalizedOptions.find(
            (o) => String(o.value) === String(raw)
          );
          const nextValue = matched ? matched.value : raw;

          setValue(nextValue);
          onChange?.(field, nextValue);

          await commitSave(nextValue);
          setEditing(false);
        }}
        onBlur={() => setEditing(false)}
        sx={{
          "& .MuiInputBase-input": {
            ...(theme.typography as any)[variant] || {},
            padding: 0,
          },
        }}
      >
        <MenuItem value="">
          <em>{placeholder ?? "Select..."}</em>
        </MenuItem>

        {normalizedOptions.map((opt) => (
          <MenuItem key={String(opt.value)} value={String(opt.value)}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    ) : (
      <Typography
        variant={variant}
        sx={{ cursor: "pointer", display: "block", mb: 1 }}
        onClick={() => setEditing(true)}
      >
        {selected === "" || selected === null || selected === undefined
          ? placeholder ?? label
          : findLabel(selected) || String(selected)}
      </Typography>
    );
  }

  // DATE / DATETIME / NUMBER / TEXT
  const inputType =
    inferredType === "date"
      ? "date"
      : inferredType === "datetime"
      ? "datetime-local"
      : inferredType === "number"
      ? "number"
      : "text";

  const isMultiline = inferredType === "multiline";

  return editing ? (
    <TextField
      variant="standard"
      fullWidth
      value={value}
      autoFocus
      multiline={isMultiline}
      type={inputType}
      placeholder={placeholder}
      onChange={(e) => {
        const next = e.target.value;
        setValue(next);
        onChange?.(field, next);
      }}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !isMultiline) handleBlur();
      }}
      inputProps={{
        min,
        max,
        step: inferredType === "number" ? step ?? "any" : step,
      }}
      InputLabelProps={
        inferredType === "date" || inferredType === "datetime"
          ? { shrink: true }
          : undefined
      }
      sx={{
        "& .MuiInputBase-input": {
          ...(theme.typography as any)[variant] || {},
          padding: 0,
        },
      }}
    />
    ) : (
    <Typography
      variant={variant}
      sx={{ cursor: "text", display: "block", mb: 1 }}
      onClick={() => setEditing(true)}
    >
      {value === "" || isNil(value)
        ? (placeholder ?? label)
        : formatForDisplay(inferredType, (record as any)?.[field])}
    </Typography>
  );
};

export default EditableField;
